import React, { ReactElement, useEffect, useState } from "react";
import { RouteComponentProps } from "react-router";
import { v7 as uuidv7 } from "uuid";
import { assertNever, assertNotNull, assertNotNullOrUndefined } from "helpers/Helpers";
import { getNextStep, StepId, getPreviousStep } from "logic/Navigation/Steps";
import { NewBooking, WorkDetails, CompleteNewBooking } from "types/NewBooking";
import { ServiceCentreSearch } from "views/NewBooking/ServiceCentreSearch";
import { ChooseDateAndTime } from "views/NewBooking/ChooseDateAndTime/ChooseDateAndTime";
import { OdometerReading } from "views/NewBooking/OdometerReading";
import { RequiredWork } from "views/NewBooking/RequiredWork";
import { Summary } from "views/NewBooking/Summary";
import { VehicleLookup } from "views/NewBooking/VehicleLookup";
import { BookingConfirmation } from "views/NewBooking/BookingConfirmation";
import { DriverDetailsInput } from "views/NewBooking/DriverDetailsInput";
import { VehicleDetails, ServiceCentreCardDetails, DriverDetails, MicrositeSetting } from "generated/types/types";
import { ClearTemporaryBooking } from "views/NewBooking/ClearTemporaryBooking";

interface MyParams {
    micrositePin: string;
}

interface Props extends RouteComponentProps<MyParams> {
    initialInputData: NewBooking;
    setInitialInputData: (_: NewBooking) => void;
    currentStep: StepId;
    setCurrentStep: (stepId: StepId) => void;
    micrositeSetting: MicrositeSetting;
    previousStep: StepId;
}

export const NewBookingRoot: React.FC<Props> = (props: Props) => {
    const [encryptedBookingId, setEncryptedBookingId] = useState<string>("");
    const [vehicleDetails, setVehicleDetails] = useState<VehicleDetails | null>(props.initialInputData.vehicleDetails);
    const [odoReading, setOdoReading] = useState<string | null>(null);
    const [workDetails, setWorkDetails] = useState<WorkDetails | null>(props.initialInputData.workDetails);
    const [selectedServiceCentre, setSelectedServiceCentre] = useState<ServiceCentreCardDetails>();
    const [selectedBookingDates, setSelectedBookingDates] = useState<Date[]>([]);
    const [driverDetails, setDriverDetails] = useState<DriverDetails | null>(props.initialInputData.driverDetails);
    const [tempBookingReference, setTempBookingReference] = useState("");
    const [currentStep, setCurrentStep] = useState<StepId>(props.currentStep);
    const [showClearTemporaryBooking, setShowClearTemporaryBooking] = useState(false);
    const [uniqueReferenceCode, setUniqueReferenceCode] = useState(uuidv7());

    useEffect(() => {
        if (props.currentStep < StepId.ChooseDateAndTime && tempBookingReference) {
            setShowClearTemporaryBooking(true);
        } else {
            setCurrentStep(props.currentStep);
        }
    }, [props.currentStep, tempBookingReference]);

    const setVehicleDetailsWithReset = (details: VehicleDetails | null) => {
        if (vehicleDetails?.registrationNumber !== details?.registrationNumber) {
            setDriverDetails(null);
        }
        setVehicleDetails(details);
    };

    const setDiaryIntegrationFlag = (isDiaryIntegrated: boolean) => {
        if (!workDetails) return;
        setWorkDetails({ ...workDetails, isDiaryIntegrated: isDiaryIntegrated });
    }

    const goToNextStep = () => {
        const nextStep = getNextStep(currentStep);

        if (nextStep === StepId.ChooseDateAndTime) {
            /* Resetting unique reference code whenever navigating to ChooseDateTime (for getAvailability api) */
            setUniqueReferenceCode(uuidv7());
        }

        if (nextStep !== null) {
            props.setCurrentStep(nextStep);
        } else {
            console.error("Cannot advance to next step as we appear to have reached the end");
        }
    };

    const goToPreviousStep = () => {
        const previousStep = getPreviousStep(currentStep);
        if (previousStep !== null) {
            props.setCurrentStep(previousStep);
        } else {
            console.error("No previous step exists");
        }
    };

    const goToCalendar = () => {
        props.setCurrentStep(StepId.ChooseDateAndTime);
    }

    const goToWorkRequired = () => {
        props.setCurrentStep(StepId.RequiredWork);
    }

    const getStepComponent = (stepId: StepId): ReactElement => {
        switch (stepId) {
            case StepId.VehicleLookup:
                return (
                    <VehicleLookup
                        currentVehicleDetails={vehicleDetails}
                        setVehicleDetails={setVehicleDetailsWithReset}
                        goToNextStep={goToNextStep}
                        micrositePin={props.match.params.micrositePin}
                    />
                );
            case StepId.OdometerReading:
                return <OdometerReading
                    odoReadingCheck={props.micrositeSetting.odoReadingCheck}
                    currentOdoReading={odoReading}
                    setOdoReading={setOdoReading}
                    goToNextStep={goToNextStep}
                    lastOdoReading={vehicleDetails?.lastOdoReading || null}
                />;
            case StepId.RequiredWork:
                return (
                    <RequiredWork
                        micrositePin={props.match.params.micrositePin}
                        vehicleDetails={assertNotNull(vehicleDetails)}
                        currentWorkDetails={workDetails}
                        setWorkDetails={setWorkDetails}
                        goToNextStep={goToNextStep}
                        motDueDate={vehicleDetails?.motDueDate ?? null}
                        showMotDueDate={props.micrositeSetting.showMotDueDate}
                        safetyRecallCheck={props.micrositeSetting.safetyRecallCheck}
                        noBookingOptionsHelptext = {props.micrositeSetting.noBookingOptionsHelptext}
                        noBookingOptionsHelptextUrl = {props.micrositeSetting.noBookingOptionsHelptextUrl}
                    />
                );
            case StepId.ServiceCentreSearch:
                return (
                    <ServiceCentreSearch
                        vehicleRegNo={assertNotNullOrUndefined(vehicleDetails?.registrationNumber)}
                        odometerReading={odoReading}
                        workDetails={assertNotNull(workDetails)}
                        setSelectedServiceCentre={setSelectedServiceCentre}
                        goToNextStep={goToNextStep}
                        goToPreviousStep={goToPreviousStep}
                        micrositePin={props.match.params.micrositePin}
                        maxGarageSearchRadius={props.micrositeSetting.maxGarageSearchRadius}
                        showServiceCenterNameGarageSearchFilter={props.micrositeSetting.showServiceCenterNameGarageSearchFilter}
                        showSiteTypeGarageSearchFilter={props.micrositeSetting.showSiteTypeGarageSearchFilter}
                        showSearchRadiusGarageSearchFilter={props.micrositeSetting.showSearchRadiusGarageSearchFilter}
                        showMainDealerGarageSearchFilter={props.micrositeSetting.showMainDealerGarageSearchFilter}
                        displayNetworkPreferenceLevels={props.micrositeSetting.displayNetworkPreferenceLevels}
                        displayCollectionDistance={props.micrositeSetting.displayCollectionDistance}
                        hideSitesOutsideCollectionRadius={props.micrositeSetting.hideSitesOutsideCollectionRadius}
                        showHiddenSites={props.micrositeSetting.showHiddenSites}
                        previousStep={props.previousStep}
                        safetyRecall={workDetails?.safetyRecall ?? false}
                        setWorkDetails={setWorkDetails}

                    />
                );
            case StepId.ChooseDateAndTime:
                return (
                    <ChooseDateAndTime
                        encryptedSiteId={assertNotNullOrUndefined(selectedServiceCentre?.id)}
                        micrositePin={props.match.params.micrositePin}
                        vehicleRegNo={assertNotNullOrUndefined(vehicleDetails?.registrationNumber)}
                        additionalServiceRequested={assertNotNullOrUndefined(workDetails?.additionalServiceRequested)}
                        workDetails={workDetails}
                        currentSelectedBookingDates={selectedBookingDates}
                        setSelectedDates={setSelectedBookingDates}
                        goToNextStep={goToNextStep}
                        calendarDaysBlockOutCollectionAndDelivery={props.micrositeSetting.calendarDaysBlockOutCollectionAndDelivery}
                        calendarDaysBlockOutCourtesyCar={props.micrositeSetting.calendarDaysBlockOutCourtesyCar}
                        calendarDaysBlockOutDropOff={props.micrositeSetting.calendarDaysBlockOutDropOff}
                        calendarDaysBlockOutWhileYouWait={props.micrositeSetting.calendarDaysBlockOutWhileYouWait}
                        minimumNumberOfDiarySlotsAllowed={props.micrositeSetting.minimumNumberOfDiarySlotsAllowed}
                        isDiaryIntegrated={selectedServiceCentre?.isDiaryIntegrated || false}
                        setDiaryIntegrationFlag={setDiaryIntegrationFlag}
                        goToPreviousStep={goToPreviousStep}
                        goToWorkRequired={goToWorkRequired}
                        referenceCode={uniqueReferenceCode}
                        setTempBookingReference={setTempBookingReference}
                        tempBookingReference={tempBookingReference}
                        odometerReading={odoReading}
                    />
                );
            case StepId.DriverDetails:
                return (
                    <DriverDetailsInput
                        encryptedSchemeId={assertNotNullOrUndefined(vehicleDetails?.scheme.encryptedId)}
                        currentWorkDetails={assertNotNull(workDetails)}
                        currentDriverDetails={driverDetails}
                        setDriverDetails={setDriverDetails}
                        goToNextStep={goToNextStep}
                        privacyPolicyUrl={props.micrositeSetting.privacyPolicyUrl}
                        serviceDeskBookerName={props.micrositeSetting.serviceDeskBookerName}
                    />
                );
            case StepId.Summary:
                return (
                    <Summary
                        currentInputData={assembleNewBookingObject()}
                        goToNextStep={goToNextStep}
                        goToCalendar={goToCalendar}
                        micrositePin={props.match.params.micrositePin}
                        setEncryptedBookingId={setEncryptedBookingId}
                        referenceCode={uniqueReferenceCode}
                    />
                );
            case StepId.BookingConfirmation:
                return <BookingConfirmation encryptedBookingId={encryptedBookingId} />;
            default:
                assertNever(stepId);
        }
    };

    const assembleNewBookingObject = (): CompleteNewBooking => {
        return {
            driverDetails: assertNotNullOrUndefined(driverDetails),
            previousBookingId: props.initialInputData.previousBookingId,
            selectedServiceCentreDetails: assertNotNullOrUndefined(selectedServiceCentre),
            vehicleDetails: { ...assertNotNull(vehicleDetails), odoReading: assertNotNullOrUndefined(odoReading) },
            workDetails: { ...assertNotNull(workDetails), requestedBookingDates: selectedBookingDates },
            tempBookingReference: tempBookingReference
        };
    };

    const onSuccessClearTemporaryBooking = () => {
        setShowClearTemporaryBooking(false);
        setCurrentStep(props.currentStep);
        setTempBookingReference("");
        setSelectedBookingDates([]);
    }

    const onCancelClearTemporaryBooking = () => {
        setShowClearTemporaryBooking(false);
        props.setCurrentStep(props.previousStep);
    }

    return (
        // TODO: Find a way to make this work with the need to scroll to the top of each page we go to
        // <TransitionGroup>
        //     <CSSTransition key={props.currentStep} timeout={200} classNames="newbooking-step">
        <>
            {getStepComponent(currentStep)}
            {
                showClearTemporaryBooking &&
                <ClearTemporaryBooking
                    apiRequest={{
                        EncryptedSiteId: selectedServiceCentre?.id || "",
                        MicrositePin: props.match.params.micrositePin,
                        VehicleReg: vehicleDetails?.registrationNumber || "",
                        TemporaryBookingReference: tempBookingReference
                    }}
                    onSuccess={onSuccessClearTemporaryBooking}
                    onCancel={onCancelClearTemporaryBooking}
                />
            }
        </>
        //     </CSSTransition>
        // </TransitionGroup>
    );
};
